import { useState, useEffect, useCallback, useMemo } from 'react';
import { useActiveWallet, useConnectWallet, useWallets } from '@privy-io/react-auth';

// Network name mapping
const getNetworkName = chainId => {
  const networkMap = {
    1: 'Ethereum Mainnet',
    56: 'BSC Mainnet',
    36900: 'ADI Network',
    11155111: 'Sepolia',
    137: 'Polygon',
    42161: 'Arbitrum',
    10: 'Optimism',
  };
  return networkMap[chainId] || `Chain ${chainId}`;
};

const getWalletNetworkConfig = targetChain => {
  const targetChainId = Number(targetChain?.networkId || 1);
  return {
    chainId: `0x${targetChainId.toString(16)}`,
    chainName: targetChain?.networkLabel || getNetworkName(targetChainId),
    nativeCurrency: targetChain?.nativeCurrency || {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
    rpcUrls: [targetChain?.rpcUrl].filter(Boolean),
    blockExplorerUrls: [
      targetChain?.blockExplorerUrl || targetChain?.explorerBaseUrl?.replace(/\/tx\/?$/, ''),
    ].filter(Boolean),
  };
};

const getProviderLabel = wallet => {
  if (wallet?.meta?.name) return wallet.meta.name;
  return 'EVM Wallet';
};

const selectEthereumWallet = ({ activeWallet, wallets }) => {
  if (activeWallet?.type === 'ethereum') return activeWallet;

  const evmWallets = wallets.filter(wallet => wallet.type === 'ethereum');
  return evmWallets.length === 1 ? evmWallets[0] : null;
};

const parseChainId = chainId => {
  if (typeof chainId === 'number') return chainId;
  if (typeof chainId !== 'string') return null;
  if (chainId.startsWith('eip155:')) return Number(chainId.split(':')[1]);
  if (chainId.startsWith('0x')) return parseInt(chainId, 16);
  return Number(chainId);
};

const selectProviderAccount = ({ accounts, activeAddress }) => {
  const normalizedActiveAddress = activeAddress ? activeAddress.toLowerCase() : '';
  if (normalizedActiveAddress) {
    const activeAccount = (accounts || []).find(
      account => String(account).toLowerCase() === normalizedActiveAddress
    );
    if (activeAccount) return activeAddress;
    return activeAddress;
  }

  return accounts?.length === 1 ? accounts[0] : '';
};

export const useWallet = targetChain => {
  const [address, setAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [currentChainId, setCurrentChainId] = useState(null);
  const [currentNetworkName, setCurrentNetworkName] = useState('');
  const [provider, setProvider] = useState(null);
  const { wallets, ready: walletsReady } = useWallets();
  const { wallet: activePrivyWallet, setActiveWallet } = useActiveWallet();
  const { connectWallet } = useConnectWallet({
    onSuccess: ({ wallet }) => {
      if (wallet?.type === 'ethereum') {
        setActiveWallet(wallet);
      }
    },
  });

  const activeWallet = useMemo(
    () => selectEthereumWallet({ activeWallet: activePrivyWallet, wallets }),
    [activePrivyWallet, wallets]
  );
  const walletProviderLabel = activeWallet ? getProviderLabel(activeWallet) : '';

  useEffect(() => {
    let cancelled = false;
    const loadProvider = async () => {
      if (!activeWallet) {
        setProvider(null);
        setAddress('');
        setIsConnected(false);
        setIsCorrectNetwork(false);
        setCurrentChainId(null);
        setCurrentNetworkName('');
        return;
      }

      setAddress(activeWallet.address || '');
      setIsConnected(Boolean(activeWallet.address));

      try {
        const activeWalletProvider = await activeWallet.getEthereumProvider();
        if (!cancelled) setProvider(activeWalletProvider);
      } catch (error) {
        console.error('Failed to load EVM wallet provider:', error);
        if (!cancelled) setProvider(null);
      }
    };

    loadProvider();
    return () => {
      cancelled = true;
    };
  }, [activeWallet]);

  const checkNetwork = useCallback(
    async nextProvider => {
      const providerToCheck = nextProvider || provider;
      if (!providerToCheck) return;

      try {
        const chainId = await providerToCheck.request({ method: 'eth_chainId' });
        const currentChainIdNum = parseChainId(chainId);
        const targetChainId = Number(targetChain?.networkId || 1);
        const isCorrect = currentChainIdNum === targetChainId;
        setIsCorrectNetwork(isCorrect);
        setCurrentChainId(currentChainIdNum);
        setCurrentNetworkName(getNetworkName(currentChainIdNum));
        return isCorrect;
      } catch (error) {
        console.error('Error checking network:', error);
        setIsCorrectNetwork(false);
        setCurrentChainId(null);
        setCurrentNetworkName('');
        return false;
      }
    },
    [provider, targetChain]
  );

  const switchNetwork = useCallback(async () => {
    if (!provider) {
      throw new Error('EVM wallet not detected');
    }

    try {
      // Get current chain ID before switching
      const currentChainIdHex = await provider.request({ method: 'eth_chainId' });
      const currentChainIdNum = parseChainId(currentChainIdHex);

      const targetChainId = Number(targetChain?.networkId || 1);
      const targetNetworkName = targetChain?.networkLabel || getNetworkName(targetChainId);

      // Check if already on the target network
      if (currentChainIdNum === targetChainId) {
        // Already on the target network, just update state and return
        await checkNetwork();
        return { success: true, alreadyOnNetwork: true, targetNetworkName };
      }

      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${targetChainId.toString(16)}` }],
      });

      // Wait for the chain to actually change by polling
      let attempts = 0;
      const maxAttempts = 20; // 2 seconds max wait
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        const newChainIdHex = await provider.request({ method: 'eth_chainId' });
        const newChainIdNum = parseChainId(newChainIdHex);
        if (newChainIdNum === targetChainId && newChainIdNum !== currentChainIdNum) {
          // Chain has changed, update state
          await checkNetwork();
          return { success: true, alreadyOnNetwork: false, targetNetworkName };
        }
        attempts++;
      }

      // If we get here, check if the chain actually changed
      const finalChainIdHex = await provider.request({ method: 'eth_chainId' });
      const finalChainIdNum = parseChainId(finalChainIdHex);
      await checkNetwork();

      if (finalChainIdNum === targetChainId) {
        return {
          success: true,
          alreadyOnNetwork: finalChainIdNum === currentChainIdNum,
          targetNetworkName,
        };
      }

      return { success: false, alreadyOnNetwork: false, targetNetworkName };
    } catch (switchError) {
      // User rejected the request
      if (switchError.code === 4001) {
        throw new Error('Network switch was rejected. Please approve the request in your wallet.');
      }

      // This error code indicates that the chain has not been added to the wallet.
      if (switchError.code === 4902) {
        try {
          const targetChainId = Number(targetChain?.networkId || 1);
          const networkConfig = getWalletNetworkConfig(targetChain);

          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [networkConfig],
          });

          // Wait for the chain to actually change by polling
          let attempts = 0;
          const maxAttempts = 20; // 2 seconds max wait
          const addTargetChainId = targetChainId;
          const addTargetNetworkName =
            targetChain?.networkLabel || getNetworkName(addTargetChainId);

          while (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 100));
            const newChainIdHex = await provider.request({ method: 'eth_chainId' });
            const newChainIdNum = parseChainId(newChainIdHex);
            if (newChainIdNum === addTargetChainId) {
              // Chain has changed, update state
              await checkNetwork();
              return {
                success: true,
                alreadyOnNetwork: false,
                targetNetworkName: addTargetNetworkName,
              };
            }
            attempts++;
          }

          // If we get here, check if the chain actually changed
          const finalChainIdHex = await provider.request({ method: 'eth_chainId' });
          const finalChainIdNum = parseChainId(finalChainIdHex);
          await checkNetwork();

          if (finalChainIdNum === addTargetChainId) {
            return {
              success: true,
              alreadyOnNetwork: false,
              targetNetworkName: addTargetNetworkName,
            };
          }

          return {
            success: false,
            alreadyOnNetwork: false,
            targetNetworkName: addTargetNetworkName,
          };
        } catch (addError) {
          if (addError.code === 4001) {
            throw new Error(
              'Network addition was rejected. Please approve adding the network in your wallet.'
            );
          }
          console.error('Failed to add network:', addError);
          throw new Error(`Failed to add network: ${addError.message || 'Unknown error'}`);
        }
      } else {
        console.error('Failed to switch network:', switchError);
        throw new Error(`Failed to switch network: ${switchError.message || 'Unknown error'}`);
      }
    }
  }, [provider, checkNetwork, targetChain]);

  const connect = useCallback(async () => {
    if (activeWallet?.address) {
      setAddress(activeWallet.address);
      setIsConnected(true);
      await checkNetwork();
      return activeWallet.address;
    }

    if (!walletsReady) {
      throw new Error('Wallet connector is still loading. Please try again.');
    }

    try {
      connectWallet({ walletChainType: 'ethereum-only' });
      return null;
    } catch (error) {
      console.error('Connection error:', error);
      throw error;
    }
  }, [activeWallet, checkNetwork, connectWallet, walletsReady]);

  const disconnect = useCallback(() => {
    setAddress('');
    setIsConnected(false);
    setIsCorrectNetwork(false);
    setCurrentChainId(null);
    setCurrentNetworkName('');
    if (activeWallet?.disconnect) {
      activeWallet.disconnect();
    }
  }, [activeWallet]);

  useEffect(() => {
    const checkExistingConnection = async () => {
      if (!provider) return;

      try {
        let accounts = [];
        try {
          accounts = await provider.request({ method: 'eth_accounts' });
        } catch {
          accounts = [];
        }
        const connectedAddress = selectProviderAccount({
          accounts,
          activeAddress: activeWallet?.address || '',
        });
        if (connectedAddress) {
          setAddress(connectedAddress);
          setIsConnected(true);
          await checkNetwork();
        }
      } catch (error) {
        console.error('Error checking existing wallet connection:', error);
        setIsCorrectNetwork(false);
      }
    };

    const handleAccountsChanged = accounts => {
      if (accounts.length > 0) {
        const connectedAddress = selectProviderAccount({
          accounts,
          activeAddress: activeWallet?.address || '',
        });
        if (connectedAddress) {
          setAddress(connectedAddress);
          setIsConnected(true);
          checkNetwork();
          return;
        }
      } else {
        setAddress('');
        setIsConnected(false);
        setIsCorrectNetwork(false);
      }
    };

    const handleChainChanged = async chainId => {
      // Update network state immediately with the new chain ID
      try {
        const currentChainIdNum = parseChainId(chainId);
        const targetChainId = Number(targetChain?.networkId || 1);
        const isCorrect = currentChainIdNum === targetChainId;
        setIsCorrectNetwork(isCorrect);
        setCurrentChainId(currentChainIdNum);
        setCurrentNetworkName(getNetworkName(currentChainIdNum));
      } catch (error) {
        // Fallback to checkNetwork if direct update fails
        await checkNetwork();
      }
    };

    checkExistingConnection();

    if (provider) {
      provider.on('accountsChanged', handleAccountsChanged);
      provider.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (provider) {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [provider, checkNetwork, targetChain, activeWallet]);

  const getTargetNetworkName = useCallback(
    () => targetChain?.networkLabel || getNetworkName(Number(targetChain?.networkId || 1)),
    [targetChain]
  );

  return {
    address,
    isConnected,
    isCorrectNetwork,
    currentChainId,
    currentNetworkName,
    toggleTargetNetworkName: getTargetNetworkName(),
    connect,
    disconnect,
    switchNetwork,
    checkNetwork,
    provider,
    walletProviderLabel,
  };
};
